import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';

const PLAN_LABELS: Record<string, string> = {
    kanea_pro: 'Kanea Pro',
    kanea_illimite: 'Kanea Illimité',
};

const PaymentSuccessScreen: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const ref = searchParams.get('ref') || searchParams.get('token');

    const [status, setStatus] = useState<'loading' | 'completed' | 'pending' | 'failed' | 'error'>('loading');
    const [planId, setPlanId] = useState('');
    const [amount, setAmount] = useState(0);

    useEffect(() => {
        if (!ref) {
            setStatus('error');
            return;
        }
        checkStatus();
        // Poll every 5s if still pending
        const interval = setInterval(() => {
            checkStatus();
        }, 5000);
        return () => clearInterval(interval);
    }, [ref]);

    const checkStatus = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/status/${ref}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Not found');
            const data = await response.json();
            setPlanId(data.plan_id);
            setAmount(data.amount);

            if (data.status === 'COMPLETED') {
                setStatus('completed');
            } else if (data.status === 'FAILED') {
                setStatus('failed');
            } else {
                setStatus('pending');
            }
        } catch {
            setStatus('error');
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }}>⏳</div>
                        <h2 style={{ color: 'var(--text-heading)', marginBottom: '8px' }}>Vérification en cours...</h2>
                        <p style={{ color: 'var(--text-muted)' }}>Nous vérifions votre paiement auprès de PayDunia.</p>
                    </div>
                );
            case 'pending':
                return (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 2s ease-in-out infinite' }}>🔄</div>
                        <h2 style={{ color: '#f59e0b', marginBottom: '8px' }}>Paiement en attente</h2>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
                            Votre paiement de <strong>{amount.toLocaleString('fr-FR')} FCFA</strong> pour <strong>{PLAN_LABELS[planId] || planId}</strong> est en cours de traitement.
                        </p>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                            Cette page se met à jour automatiquement. Vous recevrez également une confirmation par email.
                        </p>
                    </div>
                );
            case 'completed':
                return (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
                        <h2 style={{ color: '#10b981', marginBottom: '8px', fontSize: '1.8rem' }}>Paiement confirmé !</h2>
                        <p style={{ color: 'var(--text-main)', marginBottom: '24px', fontSize: '1.1rem' }}>
                            Votre abonnement <strong>{PLAN_LABELS[planId] || planId}</strong> a été activé avec succès.
                        </p>
                        <button
                            onClick={() => { window.location.href = '/'; }}
                            style={{
                                padding: '14px 40px', borderRadius: '12px', border: 'none', fontSize: '1.1rem',
                                fontWeight: '700', cursor: 'pointer', color: 'white',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                                transition: 'all 0.2s',
                            }}
                        >
                            🚀 Commencer à explorer
                        </button>
                    </div>
                );
            case 'failed':
                return (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>❌</div>
                        <h2 style={{ color: '#ef4444', marginBottom: '8px' }}>Paiement échoué</h2>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
                            Votre paiement n'a pas pu être traité. Veuillez réessayer.
                        </p>
                        <button
                            onClick={() => navigate('/upgrade')}
                            style={{
                                padding: '14px 40px', borderRadius: '12px', border: 'none', fontSize: '1rem',
                                fontWeight: '700', cursor: 'pointer', color: 'white',
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
                            }}
                        >
                            ← Réessayer
                        </button>
                    </div>
                );
            default:
                return (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                        <h2 style={{ color: 'var(--text-heading)' }}>Référence introuvable</h2>
                        <p style={{ color: 'var(--text-muted)' }}>Impossible de retrouver cette transaction.</p>
                        <button
                            onClick={() => navigate('/upgrade')}
                            style={{
                                marginTop: '20px', padding: '12px 30px', borderRadius: '10px', border: 'none',
                                fontSize: '1rem', fontWeight: '600', cursor: 'pointer', color: 'white',
                                background: '#6366f1',
                            }}
                        >
                            ← Retour
                        </button>
                    </div>
                );
        }
    };

    return (
        <SiteLayout>
            <div className='content-area'>
                <div style={{
                    maxWidth: '600px', margin: '80px auto', padding: '50px 40px',
                    background: 'var(--bg-box)', borderRadius: '20px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 10px 40px var(--shadow-color)',
                }}>
                    {renderContent()}
                </div>
            </div>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.6; transform: scale(0.95); }
                }
            `}</style>
        </SiteLayout>
    );
};

export default PaymentSuccessScreen;
